import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useParams, useSearchParams } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { useAudioPlayer } from './hooks/useAudioPlayer';
import { Navbar } from './components/Navbar';
import { AudioPlayer } from './components/AudioPlayer';
import { ArtworkDisplay } from './components/ArtworkDisplay';
import { LanguageSelector } from './components/LanguageSelector';
import { SplashScreen } from './components/SplashScreen';
import { Artwork, Language } from './types';
import { API_BASE_URL } from './config/config';
import { getArtworkAudioUrl } from './utils/artworkContent';
function ArtworkPage() {
  const { artId } = useParams<{ artId: string }>();
  const [searchParams] = useSearchParams();
  const { language, setLanguage, hasSelectedLanguage } = useLanguage();
  const audioPlayer = useAudioPlayer();
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [artwork, setArtwork] = useState<Artwork | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>('english');

  useEffect(() => {
    if (artId) {
      fetch(`${API_BASE_URL}/art/${artId}`)
          .then(res => {
            if (!res.ok) throw new Error('API error');
            return res.json();
          })
          .then(data => {
            setArtwork(data);
          })
          .catch(err => {
            console.error(err);
            setArtwork(null);
          })
          .finally(() => setLoading(false));
    }
  }, [artId]);
  const isFromQR = true

  useEffect(() => {
    console.log(isFromQR,':',hasSelectedLanguage)
    // Show language selector if from QR and no language preference set
    if (!hasSelectedLanguage && isFromQR) {
      setShowLanguageSelector(true);
    }
  }, [isFromQR, hasSelectedLanguage]);

  useEffect(() => {
    if (language) {
      setSelectedLanguage(language);
    }
  }, [language]);

  useEffect(() => {
    if (!artwork) {
      return;
    }

    const audioUrl = getArtworkAudioUrl(artwork, selectedLanguage);
    if (audioUrl) {
      audioPlayer.loadAudio(audioUrl);
    }
  }, [artwork, selectedLanguage]);

  const handleLanguageSelect = (lang: Language) => {
    setLanguage(lang);
    setSelectedLanguage(lang);
    setShowLanguageSelector(false);
  };

  const handleArtworkLanguageChange = (lang: Language) => {
    setSelectedLanguage(lang);
    setLanguage(lang);
  };

  // Not from QR and no artwork - show splash
  if (!isFromQR && !artwork) {
    return <SplashScreen />;
  }
  if (loading) {
    return <div className="p-6 text-center">Loading...</div>;
  }
  // Artwork not found
  if (!artwork) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gallery-light mb-2">
            Artwork not found
          </h1>
          <p className="text-gray-400">
            Please scan a valid QR code
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {showLanguageSelector && (
        <LanguageSelector onSelect={handleLanguageSelect} />
      )}
      
      <Navbar />
      
      <AudioPlayer
        isPlaying={audioPlayer.isPlaying}
        currentTime={audioPlayer.currentTime}
        duration={audioPlayer.duration}
        onPlay={audioPlayer.play}
        onPause={audioPlayer.pause}
        onReplay={audioPlayer.replay}
        onStop={audioPlayer.stop}
      />
      
      <ArtworkDisplay
        artwork={artwork}
        selectedLanguage={selectedLanguage}
        onLanguageChange={handleArtworkLanguageChange}
      />
    </>
  );
}

function HomePage() {
  return <SplashScreen />;
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/art/:artId" element={<ArtworkPage />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
